import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Loading } from '@carbon/react';
import { Account, DocumentImport, DocumentExport, Recycle } from '@carbon/react/icons';
import DailyTransactions from 'src/components/Dashboard/DailyTransactions';
import StatCard from 'src/components/Dashboard/StatCard';
import TransactionsCategories from 'src/components/Dashboard/TransactionsCategories';
import { useDashboardAccountingReports } from 'src/hooks/useDashboardReports';
import { useMoney } from 'src/hooks/useMoney';
import { useDashboardStore } from 'src/states/dashboard';
import { TransactionTypeEnum } from 'src/utils/accounting';

interface AccountingProps {}

const Accounting = React.memo((_: AccountingProps) => {
  const { t } = useTranslation('dashboard');
  const { decimals, formatCompact } = useMoney();
  const navigate = useNavigate();
  const {
    query: { isLoading, data: report, isRefetching },
    bucket,
  } = useDashboardAccountingReports();
  const { startDate, endDate } = useDashboardStore(
    (state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
    }),
    shallow
  );

  const handleViewDetails = useCallback(
    (transactionType: TransactionTypeEnum | null) => {
      const filteredParams = {
        transactionType: `${transactionType}` || 'all',
        ...(startDate ? { startDate: startDate.toISOString() } : {}),
        endDate: endDate.toISOString(),
      };
      navigate({
        pathname: '/accounting',
        search: `?${createSearchParams(filteredParams)}`,
      });
    },
    [startDate, endDate, navigate]
  );

  const totalIncomes =
    report?.accountingReports?.transactionsReportsByType?.find(
      (item) => item.transactionType === TransactionTypeEnum.INCOME
    )?.transactionTotal || 0;

  const totalExpenses =
    report?.accountingReports?.transactionsReportsByType?.find(
      (item) => item.transactionType === TransactionTypeEnum.EXPENSE
    )?.transactionTotal || 0;

  const totalInvestment =
    report?.accountingReports?.transactionsReportsByType?.find(
      (item) => item.transactionType === TransactionTypeEnum.INVESTMENT
    )?.transactionTotal || 0;

  const transactionsByDateRange = report?.accountingReports?.transactionsByDateRange || [];
  const accountingReportsByCategories = report?.accountingReportsByCategories || [];

  const balance = totalIncomes - totalExpenses - totalInvestment;

  return (
    <div className={'accounting'}>
      {(isLoading || isRefetching) && <Loading small />}
      <div className="stats">
        <StatCard
          title={t('balance')}
          onClick={() => handleViewDetails(null)}
          value={balance}
          format={formatCompact}
          decimals={decimals}
          icon={<Account size={36} />}
        />
        <StatCard
          title={t('totalIncomes')}
          onClick={() => handleViewDetails(TransactionTypeEnum.INCOME)}
          value={totalIncomes || 0}
          format={formatCompact}
          decimals={decimals}
          icon={<DocumentImport size={36} />}
        />
        <StatCard
          title={t('totalExpenses')}
          onClick={() => handleViewDetails(TransactionTypeEnum.EXPENSE)}
          value={totalExpenses || 0}
          format={formatCompact}
          decimals={decimals}
          icon={<DocumentExport size={36} />}
        />
        <StatCard
          title={t('totalInvestments')}
          onClick={() => handleViewDetails(TransactionTypeEnum.INVESTMENT)}
          value={totalInvestment || 0}
          format={formatCompact}
          decimals={decimals}
          icon={<Recycle size={36} />}
        />
      </div>
      <div className="stats">
        <DailyTransactions
          dateFormat={bucket === 'month' ? 'MM/YYYY' : 'DD/MM'}
          transactionsByDateRange={transactionsByDateRange}
          title={bucket === 'month' ? t('transactionsPerMonth') : t('transactionsPerDay')}
        />
      </div>
      <div className="stats">
        <TransactionsCategories
          title={t('expenseCategories')}
          transactionsCategories={accountingReportsByCategories?.filter(
            (item) => item.transactionType === TransactionTypeEnum.EXPENSE
          )}
        />
      </div>
      <div className="stats">
        <TransactionsCategories
          title={t('incomeCategories')}
          transactionsCategories={accountingReportsByCategories?.filter(
            (item) => item.transactionType === TransactionTypeEnum.INCOME
          )}
        />
      </div>
      <div className="stats">
        <TransactionsCategories
          title={t('investmentCategories')}
          transactionsCategories={accountingReportsByCategories?.filter(
            (item) => item.transactionType === TransactionTypeEnum.INVESTMENT
          )}
        />
      </div>
    </div>
  );
});

export default Accounting;
